import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportsService } from '@/services/reports';
import { ReportAccessDenied } from '@/pages/reports/components/ReportAccessDenied';
import { useAuth } from '@/contexts/auth';
import { Report } from '@/pages/reports/schemas';
import { isAppError } from '@/lib/errors';
import {
  LoadingState,
  ErrorState,
  BackAndPreview,
  ReportHeader,
} from './components';
import { MetricsSubPage } from './MetricsSubPage';

export const ReportDetailLayout: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    const loadReport = async () => {
      if (!id) {
        setError('ID do report não fornecido');
        setLoading(false);
        return;
      }
      try {
        const reportData = await reportsService.getReportById(id, user?.email);
        if (!reportData) setError('Report não encontrado');
        else setReport(reportData);
      } catch (err) {
        if (isAppError(err) && err.status === 403) {
          setAccessDenied(true);
        } else {
          setError('Erro ao carregar report');
          console.error('Erro ao carregar report:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [id, user?.email, authLoading]);

  if (loading || authLoading) {
    return <LoadingState message="Carregando Report..." />;
  }

  if (accessDenied) {
    return <ReportAccessDenied />;
  }

  if (error || !report) {
    return (
      <ErrorState
        message={error || 'Report não encontrado'}
        onBack={() => navigate('/app/reports')}
      />
    );
  }

  return (
    <div className="w-full bg-white">
      <BackAndPreview onBack={() => navigate('/app/reports')} />
      <ReportHeader
        report={report}
        reportId={id!}
        onEdit={() => navigate(`/app/reports/${id}/edit`)}
        onStartSend={() => navigate(`/app/reports/${id}/view/send`)}
      />
      <div className="w-full min-h-[400px]">
        <MetricsSubPage />
      </div>
    </div>
  );
};
